import React, { useEffect, useState } from 'react';
import { RouteComponentProps, useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQuery } from 'react-query';
import { authenticatedRequestRaw } from '../../services/api.service';
import { toast } from 'react-toastify';
import { LOADING_UI, UNLOADING_UI } from '../../redux/types';
import Button from '../../components/UI/Button';

const CompetencyReview: React.FC<RouteComponentProps> = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const params: any = useParams();
	const testId = Number(params?.id);

	const [reviewData, setReviewData] = useState<any>(null);

	const { data, isFetching } = useQuery(
		['competencyReview', testId],
		async () => {
			try {
				const response = await authenticatedRequestRaw('GET', `/competency-test/review/${testId}`);
				return response?.data?.data;
			} catch (err: any) {
				toast.error(err?.response?.data?.message || 'Gagal memuat pembahasan tes', {
					position: toast.POSITION.TOP_CENTER,
				});
				history.push(`/competency/${testId}`);
				return null;
			}
		},
		{ enabled: Boolean(testId) }
	);

	useEffect(() => {
		if (data) {
			setReviewData(data);
		}
	}, [data]);

	useEffect(() => {
		if (isFetching) {
			dispatch({ type: LOADING_UI });
		} else {
			dispatch({ type: UNLOADING_UI });
		}
	}, [isFetching, dispatch]);

	if (!reviewData) {
		return null;
	}

	const questions = reviewData.questions || [];
	const testInfo = reviewData.test || {};

	return (
		<div className="w-full p-4 max-w-4xl mx-auto pb-20">
			{/* Top Bar Summary */}
			<div className="bg-white rounded-2xl border shadow-sm p-5 mb-6">
				<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
					<div>
						<span className="inline-block bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-1 rounded-full mb-2">
							Pembahasan & Hasil
						</span>
						<h2 className="text-xl font-bold text-gray-900">{testInfo.title}</h2>
						<p className="text-gray-500 text-xs mt-1">
							Tanggal: {reviewData.scored_date ? new Date(reviewData.scored_date).toLocaleString('id-ID') : '-'}
						</p>
					</div>
					<div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl p-4 text-center min-w-[140px]">
						<div className="text-xs uppercase tracking-wider font-semibold opacity-90">Total Skor</div>
						<div className="text-3xl font-extrabold">{Math.round(Number(reviewData.score || 0) * 100) / 100}</div>
					</div>
				</div>
				<div className="mt-4 pt-4 border-t flex items-center justify-between">
					<Button outline onClick={() => history.push(`/competency/${testId}`)} className="text-xs">
						← Kembali ke Detail Tes
					</Button>
				</div>
			</div>

			{/* Questions Review List */}
			<div className="space-y-6">
				{questions.map((q: any, idx: number) => {
					const userRes = q.user_result || {};
					const isCorrect = userRes.isCorrect;
					const meta = q.metadata || {};
					const discussion = meta.discussion || meta.explanation || meta.pembahasan;
					const earnedScore = Math.round(Number(userRes.score || 0) * 100) / 100;
					const maxScore = Math.round(Number(q.score || 0) * 100) / 100;

					return (
						<div
							key={`review-q-${q.id || idx}`}
							className={`bg-white rounded-2xl border transition-all p-5 shadow-sm ${
								isCorrect ? 'border-emerald-300' : 'border-rose-200'
							}`}
						>
							{/* Question Header & Status */}
							<div className="flex flex-col mb-3 border-b pb-3 gap-2">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<span className="font-bold text-sm bg-gray-100 text-gray-800 px-2.5 py-1 rounded-lg">
											Soal #{idx + 1}
										</span>
										{meta.element || meta.kompetensi ? (
											<span className="text-xs border border-blue-200 text-blue-700 bg-blue-50/50 font-medium px-2 py-0.5 rounded">
												{meta.element || meta.kompetensi}
											</span>
										) : null}
									</div>
									<div className="flex items-center gap-2">
										<span className="text-xs text-gray-500">Bobot: {maxScore}</span>
										{isCorrect ? (
											<span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
												✓ Benar (+{Math.round(Number(earnedScore) * 100) / 100})
											</span>
										) : (
											<span className="bg-rose-100 text-rose-800 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
												✗ {earnedScore > 0 ? `Sebagian (+${Math.round(Number(earnedScore) * 100) / 100})` : 'Salah (0)'}
											</span>
										)}
									</div>
								</div>
								
								{/* Subkompetensi */}
								{meta.subElement || meta.subkompetensi ? (
									<div className="flex items-center gap-1.5 px-1">
										<div className="w-3 h-3 rounded-full border-2 border-blue-400 flex items-center justify-center">
											<div className="w-1 h-1 bg-blue-400 rounded-full"></div>
										</div>
										<span className="text-xs text-blue-900 font-medium">
											Subkompetensi: <span className="font-semibold">{meta.subElement || meta.subkompetensi}</span>
										</span>
									</div>
								) : null}
							</div>

							{/* Shared Stimulus if any */}
							{q.stimulus ? (
								<div className="bg-blue-50/60 border border-blue-200 rounded-xl p-4 mb-4 text-xs leading-relaxed text-slate-800">
									<div className="flex flex-col md:flex-row md:items-center items-start mb-3 gap-2 md:gap-3">
										<span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full uppercase">
											{q.stimulus.type !== 'text' ? q.stimulus.type : 'Wacana Bacaan'}
										</span>
										{q.stimulus.title ? (
											<h3 className="font-bold text-lg text-slate-800 m-0 leading-tight">{q.stimulus.title}</h3>
										) : null}
									</div>
									<div className="text-sm space-y-2" dangerouslySetInnerHTML={{ __html: q.stimulus.content }} />
								</div>
							) : null}

							{/* Question Text */}
							<div
								className="text-sm font-medium text-gray-900 leading-relaxed mb-4"
								dangerouslySetInnerHTML={{ __html: q.text }}
							/>

							{/* Answer Comparison */}
							<div className="bg-gray-50 rounded-xl p-3.5 mb-4 border text-xs space-y-2">
								{q.type === 'pgk_kategori' ? (
									<div>
										<div className="font-bold text-gray-700 mb-2">Evaluasi Pernyataan:</div>
										<div className="space-y-1.5">
											{(() => {
												let rawOpts = q.options as any;
												if (typeof rawOpts === 'string') {
													try { rawOpts = JSON.parse(rawOpts); } catch {}
												}
												const rows: any[] = rawOpts?.rows || [];
												return rows.map((r: any, rIdx: number) => {
													const rowKey = String(r.id ?? rIdx);
													const userChoice = (userRes.answer || {})[rowKey] || '(Tidak dijawab)';
													const expected = (q.answer || {})[rowKey];
													const isRowRight = userChoice === expected;
													return (
														<div
															key={`row-cmp-${rIdx}`}
															className={`p-2 rounded flex flex-col md:flex-row justify-between gap-1 border ${
																isRowRight ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
															}`}
														>
															<span className="text-gray-800 flex-1" dangerouslySetInnerHTML={{ __html: r.text }} />
															<span className="font-medium whitespace-nowrap">
																Jawaban Anda: <strong className={isRowRight ? 'text-emerald-700' : 'text-rose-700'}>{userChoice}</strong> | Kunci: <strong className="text-gray-900">{expected}</strong>
															</span>
														</div>
													);
												});
											})()}
										</div>
									</div>
								) : q.type === 'pgk_mcma' ? (
									<div>
										<div className="flex items-center gap-2 mb-1">
											<span className="text-gray-600">Jawaban Anda:</span>
											<span className={`font-bold ${isCorrect ? 'text-emerald-700' : 'text-rose-700'}`}>
												{Array.isArray(userRes.answer) && userRes.answer.length > 0
													? userRes.answer.join(', ')
													: '(Tidak dijawab)'}
											</span>
										</div>
										<div className="flex items-center gap-2">
											<span className="text-gray-600">Kunci Jawaban:</span>
											<span className="font-bold text-emerald-800">
												{Array.isArray(q.answer) ? q.answer.join(', ') : q.answer}
											</span>
										</div>
									</div>
								) : (
									<div>
										<div className="flex items-center gap-2 mb-1">
											<span className="text-gray-600">Jawaban Anda:</span>
											<span className={`font-bold ${isCorrect ? 'text-emerald-700' : 'text-rose-700'}`}>
												{userRes.answer ? String(userRes.answer) : '(Tidak dijawab)'}
											</span>
										</div>
										<div className="flex items-center gap-2">
											<span className="text-gray-600">Kunci Jawaban:</span>
											<span className="font-bold text-emerald-800">{String(q.answer)}</span>
										</div>
									</div>
								)}
							</div>

							{/* Explanation / Pembahasan Box */}
							{discussion ? (
								<div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-3.5 text-xs text-amber-950">
									<div className="font-bold flex items-center gap-1.5 mb-1 text-amber-900">
										<span>💡</span> Pembahasan:
									</div>
									<div className="leading-relaxed whitespace-pre-line" dangerouslySetInnerHTML={{ __html: discussion }} />
								</div>
							) : null}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default CompetencyReview;
